import { useState, useEffect, useRef } from 'react';

interface SafeImageProps {
  src?: string;
  alt: string;
  className?: string;
  title?: string;
}

const LOGO_FALLBACK = 'https://storage.readdy-site.link/project_files/e0ad0187-0f87-44ac-bc81-3163fcc0baf7/856a6a4e-c760-477d-a837-65755837c797_fb6affb0-c65b-4aac-81c4-bfd5377e0e55.webp?v=328770f95ae230a12f1326c5fd47e1ad';

export default function SafeImage({ src, alt, className = '', title }: SafeImageProps) {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [retryCount, setRetryCount] = useState(0);
  const imgRef = useRef<HTMLImageElement>(null);

  const safeSrc = src && src.trim().length > 0 ? src : '';

  // Reset when src changes
  useEffect(() => {
    if (!safeSrc) {
      setStatus('error');
      return;
    }
    setStatus('loading');
    setRetryCount(0);
  }, [safeSrc]);

  // Handle already-cached images (onLoad may fire before listener attaches)
  useEffect(() => {
    if (!safeSrc) return;
    const img = imgRef.current;
    if (img && img.complete && img.naturalHeight > 0) {
      setStatus('loaded');
    }
  }, [safeSrc, retryCount]);

  const handleLoad = () => {
    setStatus('loaded');
  };

  const handleError = () => {
    if (retryCount < 1) {
      setTimeout(() => {
        setRetryCount((c) => c + 1);
      }, 600);
    } else {
      setStatus('error');
    }
  };

  return (
    <div className="relative w-full h-full">
      {/* Loading skeleton — lightweight, no image download */}
      {status === 'loading' && (
        <div className="absolute inset-0 bg-zinc-50 animate-pulse flex flex-col items-center justify-center gap-2 p-4">
          <i className="ri-image-line text-3xl text-zinc-200" />
          <span className="text-[10px] text-zinc-300 text-center line-clamp-2">{alt}</span>
        </div>
      )}

      {/* Error fallback — logo WebP with lazy loading */}
      {status === 'error' && (
        <div className="absolute inset-0 bg-white flex flex-col items-center justify-center gap-2 p-4">
          <img
            src={LOGO_FALLBACK}
            alt={alt}
            className="max-w-[50%] max-h-[50%] object-contain opacity-50"
            loading="lazy"
            draggable={false}
          />
        </div>
      )}

      {/* Actual image */}
      {safeSrc && (
        <img
          ref={imgRef}
          key={`${safeSrc}-${retryCount}`}
          src={safeSrc}
          alt={alt}
          title={title || alt}
          className={`${className} ${status !== 'loaded' ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
          onLoad={handleLoad}
          onError={handleError}
          draggable={false}
          loading="lazy"
        />
      )}
    </div>
  );
}